import { defineStore } from "pinia";

/**
 * The rows of a Gemeinsame Ablage whose copy into Meine Ablage is queued but
 * not yet reported back (#12961).
 *
 * `POST …/item_nodes/adopt` answers 204 the moment the job is enqueued, so
 * nothing on the server says "still copying". The only client-side fact is
 * which *source* ids a 204 accepted; the job's `completed` / `failed`
 * broadcast is the word that the copying is over. Between the two, the row
 * menus read `isPending` to refuse a second copy, and the bulk bar hides its
 * entry (D1, D3 in `docs/exec-plans/12961-prevent-duplicate-adopt.md`).
 *
 * The broadcast names no source ids (the job is not changed for this), so a
 * terminal broadcast releases *every* queued row. Two overlapping copies of
 * different rows can therefore release each other a little early — the
 * window is the one job's run time, and the price is only the bug this
 * store exists for, on that one row (D1).
 *
 * A store rather than a component ref because the writer and the readers
 * never share a component: the dialog marks, the always-mounted channel
 * singleton clears, and the menus of two different pages read.
 *
 * The broadcast can outrun the 204 on a fast job, so the request takes a
 * `settleToken()` before it goes out and hands it to `markQueued`: a job that
 * reported back meanwhile means there is nothing left to wait for (D2).
 *
 * One backstop keeps a row from staying locked when the broadcast never
 * comes (D2): each id is released after the channel's own job timeout. A
 * reconnect releases nothing — the job may well still be queued.
 */
export const useSharedLibraryAdoptStore = defineStore(
  "sharedLibraryAdopt",
  () => {
    const pendingIds = ref(new Set<string>());

    // Not state: a timer handle is nothing the UI reads, and Pinia would only
    // try to serialise it.
    const releaseTimers = new Map<string, ReturnType<typeof setTimeout>>();

    // Bumped by every `clear()`, so a request can tell whether a job reported
    // back while it was out. Not state either: no UI reads it.
    let settledCount = 0;

    const release = (id: string): void => {
      const timer = releaseTimers.get(id);
      if (timer !== undefined) clearTimeout(timer);
      releaseTimers.delete(id);
      pendingIds.value.delete(id);
    };

    /** Whether this source row has a copy queued that has not reported back. */
    const isPending = computed(
      () =>
        (id: string): boolean =>
          pendingIds.value.has(id)
    );

    /** Taken before the request goes out; handed back to `markQueued`. */
    function settleToken(): number {
      return settledCount;
    }

    /**
     * Called on the 204: these source ids are now on their way — unless a job
     * reported back since `token` was taken, in which case the copy is over.
     */
    function markQueued(ids: string[], token = settledCount): void {
      if (token !== settledCount) return;

      ids.forEach((id) => {
        // A re-mark restarts the clock; the newer request is the one to wait for.
        const timer = releaseTimers.get(id);
        if (timer !== undefined) clearTimeout(timer);

        pendingIds.value.add(id);
        releaseTimers.set(
          id,
          setTimeout(() => release(id), DEFAULT_JOB_BROADCAST_TIMEOUT_MS)
        );
      });
    }

    /** Releases every row: an adopt job reported back. */
    function clear(): void {
      settledCount += 1;
      [...pendingIds.value].forEach(release);
    }

    return { pendingIds, isPending, settleToken, markQueued, clear };
  }
);
